import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import styles from './ScrollReveal.module.css'

export type ScrollRevealDirection = 'up' | 'down' | 'left' | 'right' | 'none'

export interface ScrollRevealProps extends HTMLAttributes<HTMLDivElement> {
  direction?: ScrollRevealDirection
  /** Milliseconds to wait after the element enters view. */
  delay?: number
  /** Re-hide when scrolled back out of view. */
  once?: boolean
  /** Fraction of the element that must be visible to trigger. */
  amount?: number
  children?: ReactNode
}

const directionClass: Record<ScrollRevealDirection, string | undefined> = {
  up: styles.up,
  down: styles.down,
  left: styles.left,
  right: styles.right,
  none: undefined,
}

export function ScrollReveal({
  direction = 'up',
  delay = 0,
  once = true,
  amount = 0.2,
  className,
  style,
  children,
  ...props
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const node = ref.current
    /* v8 ignore next 3 -- ref is always attached on mount; guard is for safety */
    if (!node) {
      return
    }

    // Environments without IntersectionObserver (older browsers, some test and
    // SSR-hydration setups) must still show the content.
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true)
            if (once) {
              observer.disconnect()
            }
          } else if (!once) {
            setVisible(false)
          }
        }
      },
      { threshold: amount },
    )

    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [once, amount])

  const classes = [
    styles.reveal,
    directionClass[direction],
    visible ? styles.visible : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      ref={ref}
      className={classes}
      style={{ '--reveal-delay': `${delay}ms`, ...style } as CSSProperties}
      {...props}
    >
      {children}
    </div>
  )
}
